// Server-side PDF export for AicaReportV2 — serialises the live screen preview
// into self-contained HTML and POSTs it to aica (`/document/pdf/render`), which
// invokes the headless-Chromium Lambda server-side and relays the binary PDF.
// The client never holds the Lambda URL; the request rides the app's
// authenticated axios stack via `aicaBaseUrl`. (PDF render + image proxy live on
// aica, mirroring the RECUR-26175 editor exporter — the earlier Polus BFF
// `/unifier/pdf/render` route is abandoned.) The exported PDF is WYSIWYG with
// the preview: same DOM, same CSS, same fonts.
//
// Pagination model — UNIFORM A4 PAGES with content flow + smart break-inside:
//   Every page is a fixed A4 sheet (uniform width — no per-page sizing). The
//   cover is one full-bleed A4 page; each module starts on a fresh sheet and is
//   allowed to FLOW across as many A4 sheets as it needs. To avoid splitting a
//   card / charge row / month-grid / chart mid-element, every element that is
//   small enough to fit on a page is tagged `break-inside: avoid` so it moves
//   whole to the next sheet; only tall containers stay breakable so their
//   children paginate. The tagging is HEIGHT-DRIVEN (measured in an offscreen
//   clone), not class-driven, because the report's primitives are largely
//   inline-styled with no stable class hooks.
//
// Why this replaced the earlier "one natural-height page per module" model:
//   That model sized each page to a measured height and bound it to a named
//   @page. When the Lambda rendered a chart-bearing module taller than measured
//   (a width:100% viewBox SVG measures short in the clone), the content
//   overflowed the named page; Chromium then ABANDONS the named @page entirely
//   and drops the overflow onto its A3 default sheet — producing stray
//   wrong-width pages and clipped/missing content. A single uniform @page with
//   normal flow has no named-page to abandon, so that failure mode is gone.

import axios from 'axios';
import { inlineImagesToDataUris, waitForImages } from '../../../common/PlateEditor/exporters/exportHelpers';
import { aicaBaseUrl } from '../../../utils/axios-interceptor';
import { collectFullCss, inlineFonts, gzipToBytes, triggerDownload } from './pdfExportHelpers';
import { coverDesign, ptToPx } from '../data/designTokens';

interface ExportReportPdfServerOptions {
    /** Root element wrapping the rendered <ReportPreview>. */
    target: HTMLElement;
    filename?: string;
    /** When true, return the Blob instead of triggering a download. */
    returnBlob?: boolean;
}

// Uniform page size = the cover's A4 dimensions in CSS px (595×842pt).
const PAGE_W = Math.round(ptToPx(coverDesign.pageWidth));
const A4_PX = Math.ceil(ptToPx(coverDesign.pageHeight));
// Usable page height (margin:0, so the full sheet). An element taller than the
// threshold is treated as a breakable CONTAINER (its children paginate); one at
// or below it is an atomic block tagged `break-inside: avoid` so it never
// splits mid-element. Kept comfortably below A4 so a tagged block always fits a
// fresh sheet with room to spare.
const MAX_UNBREAKABLE_PX = Math.round(A4_PX * 0.86);
// Top-level preview "pages": the cover and every ScreenPage (TOC, each module,
// per-director details, disclaimer all use a *ScreenPage* CSS-module class).
const PAGE_SELECTOR = '[data-report-cover], [class*="ScreenPage"]';

// Vertical breathing room applied as the @page margin on EVERY content sheet
// (incl. continuations). It must come from the @page margin, not the page box's
// own padding, so a module that flows across sheets — and any element pushed to
// a fresh sheet (whose own top margin CSS collapses at the page break) — still
// gets consistent top/bottom space instead of butting the page edge.
const PAGE_MARGIN_Y = 40;

const PRINT_CSS = `
      @page { size: ${PAGE_W}px ${A4_PX}px; margin: ${PAGE_MARGIN_Y}px 0; }
      @page rcover { size: ${PAGE_W}px ${A4_PX}px; margin: 0; }
      html, body { margin: 0; padding: 0; background: #ffffff; }
      *, *::before, *::after { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
      [data-report-export-root] * { transition: none !important; animation: none !important; }

      /* Every page element: A4 width, screen chrome stripped, starts on a fresh
         sheet, and is itself breakable so a tall module flows across sheets. */
      [data-report-pg] {
        width: ${PAGE_W}px !important;
        max-width: ${PAGE_W}px !important;
        box-sizing: border-box !important;
        margin: 0 auto !important;
        border: none !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        break-before: page;
        page-break-before: always;
        break-inside: auto;
      }
      /* Content pages: vertical breathing comes from the @page margin (uniform on
         every sheet), so zero the box's own top/bottom padding to avoid a fatter
         first sheet; keep the horizontal padding for side gutters. */
      [data-report-pg]:not([data-report-cover]) {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
      }
      /* Cover: its own full-bleed @page (margin 0), one A4 sheet, internal design
         padding kept. */
      [data-report-cover] {
        page: rcover;
        height: ${A4_PX}px !important;
        min-height: 0 !important;
        overflow: hidden !important;
        break-before: auto;
        page-break-before: auto;
        break-after: page;
        page-break-after: always;
      }
      /* Charts and images never split across a sheet boundary. */
      [data-report-pg] svg, [data-report-pg] img {
        break-inside: avoid;
        page-break-inside: avoid;
        max-width: 100% !important;
      }
      /* Native tables (Audited Statements) paginate natively: the body flows
         across sheets, the column header repeats on every sheet, and individual
         rows stay whole. tagBreakInside() deliberately skips table elements so
         these rules are the single source of truth for table breaking. */
      [data-report-pg] table, [data-report-pg] tbody {
        break-inside: auto;
        page-break-inside: auto;
      }
      [data-report-pg] thead { display: table-header-group; }
      [data-report-pg] tr {
        break-inside: avoid;
        page-break-inside: avoid;
      }
      /* A heading should stay with the content that follows it. */
      [data-report-pg] h1, [data-report-pg] h2, [data-report-pg] h3,
      [data-report-pg] h4, [data-report-pg] [class*="Header"], [data-report-pg] [class*="Kicker"] {
        break-after: avoid;
        page-break-after: avoid;
      }
`;

// Give every responsive `<svg width="100%" viewBox=...>` (no explicit height) a
// concrete pixel height from its container width and viewBox aspect ratio. Such
// SVGs (ScreenDelayBars filing trend, the line/bar exposure chart) otherwise
// resolve their height unreliably in the offscreen clone — they measure short,
// which corrupts the height-driven break tagging. The baked inline height also
// travels in the serialised HTML so the Lambda renders the exact height.
function bakeResponsiveSvgHeights(root: HTMLElement): void {
    root.querySelectorAll('svg').forEach((svg) => {
        if (svg.getAttribute('height') || svg.style.height) return;
        const vb = svg.getAttribute('viewBox');
        if (!vb) return;
        const parts = vb.split(/[\s,]+/).map(Number);
        const vbW = parts[2];
        const vbH = parts[3];
        if (!Number.isFinite(vbW) || !Number.isFinite(vbH) || vbW <= 0 || vbH <= 0) return;
        const containerWidth = svg.parentElement?.clientWidth || svg.getBoundingClientRect().width || 0;
        if (containerWidth <= 0) return;
        svg.style.height = `${Math.ceil((containerWidth * vbH) / vbW)}px`;
    });
}

// Height-driven break tagging (class-agnostic). For each module page, every
// descendant that fits within a sheet is marked `break-inside: avoid` so it can
// never be sliced mid-element; taller elements are left breakable so their
// children paginate. Runs in the attached offscreen clone so getBoundingClientRect
// reflects real layout, and the inline styles travel to the Lambda.
// Native table structure must paginate natively. `break-inside: avoid` on a
// `<table>`/`<tbody>` — or on any block that WRAPS one — makes a tall financial
// table (Audited Statements) orphan its sub-heading onto a near-blank sheet and
// then break awkwardly, because the avoid can't be honoured once the element is
// taller than a page. Skip these so the body flows; row-level integrity + header
// repetition are handled by the `table`/`tr`/`thead` rules in PRINT_CSS instead.
const TABLE_TAGS = new Set(['TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'COLGROUP', 'COL']);

function tagBreakInside(root: HTMLElement): void {
    root.querySelectorAll<HTMLElement>('[data-report-pg]').forEach((pageEl) => {
        // The cover is a single full-bleed sheet — never tag its internals.
        if (pageEl.hasAttribute('data-report-cover')) return;
        pageEl.querySelectorAll<HTMLElement>('*').forEach((el) => {
            if (TABLE_TAGS.has(el.tagName)) return;
            if (el.querySelector('table')) return; // a wrapper around a table stays breakable
            const h = el.getBoundingClientRect().height;
            if (h > 0 && h <= MAX_UNBREAKABLE_PX) {
                el.style.setProperty('break-inside', 'avoid');
                el.style.setProperty('page-break-inside', 'avoid');
            }
        });
    });
}

// Strip any @page at-rule from collected host CSS. CSS-modules do NOT scope
// @page, so TextEditor.module.scss's `@page { size: auto }` leaks into the
// bundle and would fight our single A4 @page (the Lambda resolves `size:auto`
// to its A3 default). We supply our own @page, so the host ones are pure
// interference.
function stripHostPageRules(css: string): string {
    return css.replace(/@page[^{]*\{[^}]*\}/gi, '');
}

async function buildReportHtml(previewRoot: HTMLElement, filename: string): Promise<string> {
    const clone = previewRoot.cloneNode(true) as HTMLElement;

    // Flatten: re-parent every page element into a flat root in document order,
    // dropping the transition/section wrappers so no wrapper margin offsets a
    // page from its sheet top. querySelectorAll is in document order; appendChild
    // moves the node.
    // Drop the per-ScreenPage running FOOTER. It's pinned to the bottom of each
    // (single-page) preview page, but in the flow model a module spans several
    // sheets — the footer then orphans onto its own near-empty continuation
    // sheet (the stray blank page). The running header stays: it sits at a
    // module's top and never orphans.
    clone.querySelectorAll('[class*="RunningFooter"]').forEach((el) => el.remove());

    const pageEls = Array.from(clone.querySelectorAll<HTMLElement>(PAGE_SELECTOR));
    const root = document.createElement('div');
    root.setAttribute('data-report-export-root', '');
    pageEls.forEach((el) => {
        el.setAttribute('data-report-pg', '');
        // Pin width + strip chrome inline so it wins over the element's own
        // inline styles (the cover sets inline width/min-height). Border removed
        // here so it matches PRINT_CSS (and the cover keeps its inline bg).
        el.style.setProperty('width', `${PAGE_W}px`, 'important');
        el.style.setProperty('max-width', `${PAGE_W}px`, 'important');
        el.style.setProperty('margin', '0', 'important');
        el.style.setProperty('box-shadow', 'none', 'important');
        el.style.setProperty('border-radius', '0', 'important');
        el.style.setProperty('border', 'none', 'important');
        root.appendChild(el);
    });

    // Attach offscreen (visible layout, just off-canvas) at the exact page width
    // so flex-wrap / %-widths resolve as they will in print, then inline images,
    // wait for decode + fonts, bake SVG heights, and tag break-inside — all
    // before serialising.
    const host = document.createElement('div');
    host.style.cssText = `position:fixed;left:-100000px;top:0;width:${PAGE_W}px;background:#fff;`;
    host.appendChild(root);
    document.body.appendChild(host);

    try {
        await inlineImagesToDataUris(root);
        await waitForImages(root);
        const anyDoc = document as any;
        if (anyDoc.fonts?.ready) {
            try {
                await anyDoc.fonts.ready;
            } catch {
                /* best-effort */
            }
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        root.offsetHeight;
        bakeResponsiveSvgHeights(root);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        root.offsetHeight;
        tagBreakInside(root);

        const [rawHostStyles, fontStyles] = await Promise.all([collectFullCss(), inlineFonts()]);
        const hostStyles = stripHostPageRules(rawHostStyles);
        const safeTitle = filename.replace(/[<>&"]/g, '');
        const bodyContent = root.outerHTML;

        return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${safeTitle}</title>
    ${hostStyles}
    ${fontStyles}
    <style>${PRINT_CSS}</style>
  </head>
  <body>${bodyContent}</body>
</html>`;
    } finally {
        host.remove();
    }
}

// Production path: POST the gzipped HTML to aica (`/document/pdf/render`), which
// invokes the headless-Chromium Lambda server-side and relays the binary PDF, so
// the client never holds the Lambda URL and the request rides the app's
// authenticated axios stack via `aicaBaseUrl`. The gzip bytes are the raw
// request body (`application/gzip`); `fileName` rides as a query param — no
// JSON envelope (aica is the app backend, not the Polus Unifier).

// The render Lambda answers HTTP 200 even when it can't produce a PDF (e.g. a
// request it can't process) — the response body is a small JSON error
// envelope ({"responseData":{"responseCode":40,"responseMessage":"..."}}, a
// couple hundred bytes) instead of binary PDF bytes. `responseType: 'blob'`
// never inspects the body, so without this check that JSON envelope silently
// becomes the "PDF" handed to the user — aica has no inline-render fallback,
// so every render failure now arrives this way. Detected via a size floor (a
// real rendered PDF is always far bigger than any JSON error body) plus the
// `%PDF` magic-number check. Mirrors exportToPdfServer.ts's own
// assertPdfResponse, kept as a separate copy per this file's own
// don't-share-code precedent (see this file's top-of-file comment).
const MIN_PLAUSIBLE_PDF_BYTES = 2048; // the Lambda's JSON error envelope is ~197 bytes.
const PDF_MAGIC_BYTES = '%PDF';

async function assertPdfResponse(blob: Blob): Promise<void> {
    const header = await blob.slice(0, PDF_MAGIC_BYTES.length).text();
    if (blob.size >= MIN_PLAUSIBLE_PDF_BYTES && header === PDF_MAGIC_BYTES) return;

    const snippet = await blob.text().catch(() => '<unreadable response body>');
    console.error('[exportReportPdfServer] render service returned an error instead of a PDF:', snippet.slice(0, 500));
    throw new Error(`render service returned a non-PDF response (${blob.size} bytes): ${snippet.slice(0, 500)}`);
}

// Exported (mirrors exportToPdfServer.ts's own fetchPdfFromAica precedent) so
// the request shape and the non-PDF-response detection are directly
// unit-testable without the gzip pipeline — jsdom has neither
// CompressionStream nor Blob.prototype.stream.
export async function fetchPdfFromAica(gz: Blob, fileName: string): Promise<Blob> {
    const response = await axios({
        baseURL: aicaBaseUrl,
        url: '/document/pdf/render',
        method: 'POST',
        data: gz,
        headers: { 'Content-Type': 'application/gzip' },
        params: { fileName },
        responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    await assertPdfResponse(blob);
    return blob;
}

export async function exportReportPdfServer({
    target,
    filename = 'aica-report',
    returnBlob = false,
}: ExportReportPdfServerOptions): Promise<Blob | void> {
    const html = await buildReportHtml(target, filename);
    const gz = await gzipToBytes(html);

    const blob = await fetchPdfFromAica(gz, filename);

    if (returnBlob) return blob;
    triggerDownload(blob, filename);
}
